import { canvasRunLeft } from '@shared/canvasGeometry';
import { checkBrandReady } from './brand-readiness';
import { useState, useRef, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Folder, Loader2, X, Plus, ChevronRight, FileText } from 'lucide-react';
import { useCanvasStore } from './canvas-store';
import { useToast } from '@/hooks/use-toast';
import { resolveCanvasProduct } from './canvas-product-choice';

interface CloneDestinationPickerProps {
  imageUrl: string;
  imageName: string;
  /**
   * - `chat-attach` (default, legacy): attach the image to the destination
   *   chat's input box. Used by the canvas-ad-templates-view standalone
   *   page where the user is going to type their own follow-up.
   * - `clone-to-canvas`: drop the image onto the destination task's canvas
   *   and auto-fire N variations (controlled by `autoVariationsCount`).
   *   Used by the single-template Ad Library Clone button.
   * - `import-to-canvas`: await the caller's persistence-only import; no product
   *   choice, readiness gate, chat attachment, or generation. Requires onPicked.
   * - `video-clone`: project selection ONLY — no canvas drop, no chat attach.
   *   The caller MUST pass `onPicked` (which short-circuits every destination
   *   handler) and runs the video-clone prepare flow itself. This mode only
   *   changes the picker's labels.
   */
  mode?: 'chat-attach' | 'clone-to-canvas' | 'video-clone' | 'import-to-canvas';
  /** Required when mode === 'clone-to-canvas'. */
  autoVariationsCount?: number;
  /**
   * Optional preview thumbnails to show in the header. When provided, the
   * picker renders a thumbnail strip + "Clone N Ads — Choose Brand" title.
   * Used by the multi-select batch flow.
   */
  templates?: Array<{ imageUrl: string; name: string }>;
  /**
   * Optional callback that fires when the user picks a destination. When set,
   * the picker DOESN'T do the canvas-drop itself — it just hands the chosen
   * projectId back to the parent (used by the batch flow, which runs its
   * own multi-source layout + parallel clone runners).
   */
  /** v5.40 — video-clone mode passes productId too when the destination
   *  brand has 2+ products (the in-picker product step below). */
  onPicked?: (projectId: number, productId?: number, productName?: string) => void | Promise<void>;
  onClose: () => void;
}

interface Project {
  id: number;
  name: string;
  color?: string;
  isFolder: boolean;
  parentId?: number | null;
  brandId?: number | null;
}

export function CloneDestinationPicker({ imageUrl, imageName, onClose, mode = 'chat-attach', autoVariationsCount = 2, templates, onPicked }: CloneDestinationPickerProps) {
  const isImport = mode === 'import-to-canvas';
  const [isImporting, setIsImporting] = useState(false);
  const importingRef = useRef(false);
  const createdImportProject = useRef<number | null>(null);
  const importInto = async (projectId: number) => {
    if (importingRef.current) return;
    importingRef.current = true;
    setIsImporting(true);
    setProductError('');
    try {
      if (!onPicked) throw new Error('No import destination handler');
      await onPicked(projectId);
      onClose();
    } catch (error) {
      setProductError(error instanceof Error ? error.message : 'Could not add images. Please try again.');
    } finally { importingRef.current = false; setIsImporting(false); }
  };
  const productRequest=useRef(0);
  useEffect(()=>()=>{productRequest.current++},[]);
  const [productError,setProductError]=useState('');
  const isBatch = !!templates && templates.length > 1;
  const batchCount = templates?.length ?? 0;
  const [expandedFolders, setExpandedFolders] = useState<Set<number>>(new Set());
  // v6.67 — the task name is a real, editable VALUE prefilled with the
  // auto-name (Dan: "I should be able to name it … but just use that name as
  // default on load"). Previously the auto-name lived in the placeholder, so
  // the field read as greyed-out/locked.
  const [newTaskName, setNewTaskName] = useState(() =>
    isImport ? (isBatch ? `References (${batchCount} ads)` : imageName) : isBatch ? `Clone Batch (${batchCount} ads)` : `Clone: ${imageName}`,
  );
  const [selectedFolderId, setSelectedFolderId] = useState<number | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const creatingRef = useRef(false);
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { switchProject, setActiveView, addChatAttachment } = useCanvasStore();
  const persistenceProjectId = useCanvasStore((s) => s.activeProjectId);

  /**
   * Drop the source image onto the given project's canvas, select it, and
   * queue `autoVariationsCount` Create Variations to auto-fire. Used by the
   * `clone-to-canvas` mode.
   *
   * IMPORTANT: this explicitly resolves the project's brand_id and writes it
   * into `activeBrandId` before queueing the variations. Without this, the
   * variation endpoint never receives `brandId` and skips the brand's product
   * photo + packaging description — making the variations "ignore" the
   * brand's packaging entirely. switchProject only sets activeBrandId on
   * initial app load; it doesn't refresh it on subsequent switches.
   */
  const dropOnCanvasAndQueueVariations = async (projectId: number, productId?: number, productName?: string) => {
    const { addImageToCanvas, setSelectedImageIds, setCanvasOpen } = useCanvasStore.getState();
    setActiveView('chat-canvas');
    setCanvasOpen(true);

    // Resolve and hydrate brand from the destination project so the
    // variations endpoint receives a brandId and can pull the product photo.
    try {
      const projResp = await fetch(`/api/canvas/projects/${projectId}`);
      if (projResp.ok) {
        const proj = await projResp.json();
        const resolvedBrandId = proj.brandId ?? proj.brand_id ?? null;
        const resolvedAdAccountId = proj.adAccountId ?? proj.ad_account_id ?? null;
        if (resolvedBrandId) {
          useCanvasStore.setState({ activeBrandId: resolvedBrandId });
        }
        if (resolvedAdAccountId) {
          useCanvasStore.setState({ activeAdAccountId: resolvedAdAccountId });
        }
      }
    } catch (err) {
      console.warn('[CloneDestinationPicker] failed to resolve brand for project', projectId, err);
    }

    const dims = await new Promise<{ w: number; h: number }>((resolve, reject) => {
      const img = new window.Image();
      img.onload = () => resolve({ w: img.naturalWidth || 1080, h: img.naturalHeight || 1080 });
      img.onerror = () => reject(new Error('Image failed to load'));
      img.src = imageUrl;
    });

    // v6.73 — explicit drop position with RUN CLEARANCE below all content
    // (images AND in-flight placeholders). The source + its clones get a
    // labeled frame that extends 52px above the source plus a label pill;
    // auto-placement's 24-48px gaps put that frame on top of the row above.
    const projState = useCanvasStore.getState().projects[projectId];
    const RUN_CLEARANCE = 110;
    const contentBottom = [
      ...(projState?.images ?? []).map((im) => im.y + im.displayHeight),
      ...Object.values(projState?.canvasPlaceholders ?? {}).map((ph) => ph.y + ph.height),
    ].reduce((max, b) => Math.max(max, b), 0);
    const dropY = contentBottom > 0 ? contentBottom + RUN_CLEARANCE : 60;

    const imagesBefore = (useCanvasStore.getState().projects[projectId]?.images ?? []).map((i) => i.id);
    await addImageToCanvas(
      imageUrl,
      dims.w,
      dims.h,
      imageName || `template-${Date.now()}.jpg`,
      dims.w,
      dims.h,
      undefined,
      canvasRunLeft([...(projState?.images ?? []), ...Object.values(projState?.canvasPlaceholders ?? {})]),
      dropY,
    );
    const imagesAfter = useCanvasStore.getState().projects[projectId]?.images ?? [];
    const newImage = imagesAfter.find((i) => !imagesBefore.includes(i.id)) ?? imagesAfter[imagesAfter.length - 1];
    if (!newImage) throw new Error('Image not found after add');

    setSelectedImageIds([newImage.id]);
    useCanvasStore.setState({
      pendingAutoVariations: { imageId: newImage.id, count: autoVariationsCount, mode: 'clone', productId, productName },
    });

    const brandIdForToast = useCanvasStore.getState().activeBrandId;
    toast({
      title: 'Cloning template',
      description: brandIdForToast
        ? `Generating ${autoVariationsCount} clone${autoVariationsCount === 1 ? '' : 's'} of "${imageName}"${productName ? ` for ${productName}` : ''} — re-skinned to the brand's colors, product and copy…`
        : `Generating ${autoVariationsCount} clone${autoVariationsCount === 1 ? '' : 's'} of "${imageName}" — no brand attached, packaging won't be enforced.`,
    });
  };

  const { data: allProjects = [], isLoading } = useQuery<Project[]>({
    queryKey: ['/api/canvas/projects', 'all'],
    queryFn: async () => {
      const resp = await fetch('/api/canvas/projects');
      if (!resp.ok) return [];
      return resp.json();
    },
    staleTime: 30000,
  });

  const folders = allProjects.filter((p) => p.isFolder && p.brandId);
  const tasksInFolder = (folderId: number) => allProjects.filter((p) => !p.isFolder && p.parentId === folderId);

  const toggleFolder = (folderId: number) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) next.delete(folderId); else next.add(folderId);
      return next;
    });
  };

  // v5.40 — video-clone product step: the clone features EXACTLY ONE product,
  // and multi-product brands must be asked WHICH (Dan's Peta clone scripted
  // "SuperCALM and SuperFocus" over a table of SuperCALM only). The step
  // lives INSIDE this modal because picking a task navigates the app (the
  // ad-library view unmounts — a picker rendered there dies before showing).
  const [productStep, setProductStep] = useState<{
    projectId: number;
    products: Array<{ id: number; name: string }>;
  } | null>(null);

  // Product lookups must finish successfully before navigation or generation.
  const maybeAskProduct = async (projectId: number): Promise<boolean> => {
    const asks = mode === 'video-clone' ? !!onPicked : mode === 'clone-to-canvas';
    if (!asks) return false;
    const request=++productRequest.current;
    setProductError('');
    try{
      const response=await fetch(`/api/canvas/projects/${projectId}`);
      if(!response.ok)throw new Error('Could not check this task’s products. Please try again.');
      const project=await response.json();
      const brandId=project.brandId??project.brand_id;
      if(!brandId)throw new Error('Choose a task with a brand before cloning.');
      const decision=await resolveCanvasProduct({brandId,projectId,imageUrl});
      if(request!==productRequest.current)return true;
      if(decision.status==='choice_required')setProductStep({projectId,products:decision.candidates});
      else await finishWithProduct(projectId,decision.productId);
    }catch(error){if(request===productRequest.current)setProductError(error instanceof Error?error.message:'Could not check products. Please try again.');}
    return true;
  };

  const finishWithProduct = async (projectId: number, productId?: number, productName?: string) => {
    if (!await destinationReady(projectId)) return;
    await switchProject(projectId);
    if (onPicked) {
      onPicked(projectId, productId, productName);
    } else if (mode === 'clone-to-canvas') {
      try {
        await dropOnCanvasAndQueueVariations(projectId, productId, productName);
      } catch (err) {
        console.error('Clone to canvas error:', err);
        toast({ title: 'Clone failed', description: 'See console for details.', variant: 'destructive' });
      }
    }
    onClose();
  };

  const destinationReady = async (projectId: number): Promise<boolean> => {
    const response = await fetch(`/api/canvas/projects/${projectId}`);
    if (!response.ok) { toast({title:'Could not open this task',description:'Please try again.',variant:'destructive'}); return false; }
    const project = await response.json();
    const brandId = project.brandId ?? project.brand_id;
    if (!brandId) { toast({title:'Choose a brand',description:'Select a task belonging to a brand before cloning.',variant:'destructive'}); return false; }
    const message = await checkBrandReady(brandId);
    if (message) { toast({title:'Brand not ready',description:message,variant:'destructive'}); return false; }
    return true;
  };

  const handleSelectExistingTask = async (taskId: number) => {
    if (isImport) { await importInto(taskId); return; }
    if (!await destinationReady(taskId)) return;
    console.log('[ClonePicker] handleSelectExistingTask', { taskId, isBatch, hasOnPicked: !!onPicked, mode });
    if (await maybeAskProduct(taskId)) return;
    await switchProject(taskId);
    if (onPicked) {
      console.log('[ClonePicker] → calling onPicked(taskId) (batch path)');
      onPicked(taskId);
    } else if (mode === 'clone-to-canvas') {
      console.log('[ClonePicker] → falling back to dropOnCanvasAndQueueVariations (single clone path)');
      try {
        await dropOnCanvasAndQueueVariations(taskId);
      } catch (err) {
        console.error('Clone to canvas error:', err);
        toast({ title: 'Clone failed', description: 'See console for details.', variant: 'destructive' });
      }
    } else {
      setActiveView('chat-only');
      addChatAttachment({ url: imageUrl, name: imageName });
    }
    onClose();
  };

  const handleAddToCurrentChat = async () => {
    if (isImport && persistenceProjectId) { await importInto(persistenceProjectId); return; }
    if (!persistenceProjectId || !await destinationReady(persistenceProjectId)) return;
    if (onPicked && persistenceProjectId) {
      if (await maybeAskProduct(persistenceProjectId)) return;
      onPicked(persistenceProjectId);
    } else if (mode === 'clone-to-canvas' && persistenceProjectId) {
      if (await maybeAskProduct(persistenceProjectId)) return;
      try {
        await dropOnCanvasAndQueueVariations(persistenceProjectId);
      } catch (err) {
        console.error('Clone to canvas error:', err);
        toast({ title: 'Clone failed', description: 'See console for details.', variant: 'destructive' });
      }
    } else {
      addChatAttachment({ url: imageUrl, name: imageName });
      setActiveView('chat-only');
    }
    onClose();
  };

  const handleCloneToNew = async () => {
    if (isImport && createdImportProject.current) { await importInto(createdImportProject.current); return; }
    const taskName = newTaskName.trim() || (isImport ? (isBatch ? `References (${batchCount} ads)` : imageName) : isBatch ? `Clone Batch (${batchCount} ads)` : `Clone: ${imageName}`);
    // v6.62 — brand-first (Dan's spec): every task is created under a brand.
    // Resolve from the picked folder first, then the active brand; block the
    // brand-less "New task" path that used to mint brand_id NULL rows.
    const resolvedBrandId = (selectedFolderId
      ? allProjects.find((p) => p.id === selectedFolderId)?.brandId
      : null);
    if (resolvedBrandId == null) {
      toast({
        title: 'Pick a brand first',
        description: 'Please select which brand you would like to work with — choose a brand folder, or open the brand before cloning.',
        variant: 'destructive',
      });
      return;
    }
    if (creatingRef.current) return;
    creatingRef.current = true;
    setIsCreating(true);
    try {
      const readinessError = isImport ? null : await checkBrandReady(resolvedBrandId);
      if (readinessError) { toast({title:'Brand not ready',description:readinessError,variant:'destructive'}); return; }
      const resp = await fetch('/api/canvas/projects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: taskName, color: '#6366F1', parentId: selectedFolderId ?? null, isFolder: false, brandId: resolvedBrandId }),
      });
      if (!resp.ok) throw new Error('Failed to create task');
      const proj = await resp.json();
      queryClient.invalidateQueries({ queryKey: ['/api/canvas/projects', 'all'] });
      queryClient.invalidateQueries({ queryKey: ['/api/canvas/projects', 'rootOnly'] });
      if (selectedFolderId) {
        queryClient.invalidateQueries({ queryKey: ['/api/canvas/projects', selectedFolderId, 'tasks'] });
      }
      if (isImport) { createdImportProject.current = proj.id; await importInto(proj.id); return; }
      if (await maybeAskProduct(proj.id)) return;
      await switchProject(proj.id);
      if (onPicked) {
        onPicked(proj.id);
      } else if (mode === 'clone-to-canvas') {
        await dropOnCanvasAndQueueVariations(proj.id);
      } else {
        setActiveView('chat-only');
        addChatAttachment({ url: imageUrl, name: imageName });
      }
      onClose();
    } catch (err) {
      console.error('Clone to new task error:', err);
      toast({ title: isImport ? 'Could not create canvas' : 'Clone failed', description: 'Please try again.', variant: 'destructive' });
    } finally {
      creatingRef.current = false;
      setIsCreating(false);
    }
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}
      onClick={isImporting ? undefined : onClose}
    >
      <div
        data-testid={isImport ? "import-destination-picker" : "clone-destination-picker"}
        style={{ position: 'relative', background: '#FFFFFF', borderRadius: 16, border: '1px solid #E5E2DC', boxShadow: '0 24px 60px rgba(0,0,0,0.18)', width: 440, maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* v5.40 — product step (video-clone, multi-product brands): covers the
            task list until the user says WHICH product the whole clone —
            script, voiceover, visuals — should feature. */}
        {productError&&<p role="alert" style={{padding:16,color:'#b91c1c'}}>{productError}</p>}
        {productStep && (
          <div data-testid="clone-product-picker" style={{ position: 'absolute', inset: 0, zIndex: 5, background: '#FFFFFF', display: 'flex', flexDirection: 'column', padding: '20px 22px', overflowY: 'auto' }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#1A1A1A', marginBottom: 4 }}>Which product is this ad for?</div>
            <div style={{ fontSize: 12, color: '#6B6760', marginBottom: 14 }}>
              {mode === 'video-clone'
                ? 'The whole clone — script, voiceover, and visuals — features exactly this one product.'
                : `The clone${isBatch ? 's' : ''} get${isBatch ? '' : 's'} rebuilt around this product — its packaging, its colors, and props from its world.`}
            </div>
            {productStep.products.map((p) => (
              <button
                key={p.id}
                data-testid={`clone-product-${p.id}`}
                onClick={() => {
                  const step = productStep;
                  setProductStep(null);
                  void finishWithProduct(step.projectId, p.id, p.name);
                }}
                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 12px', marginBottom: 6, borderRadius: 8, border: '1px solid #E5E2DC', background: '#FAF9F6', color: '#1A1A1A', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#F0EDE6')}
                onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = '#FAF9F6')}
              >
                {p.name}
              </button>
            ))}
            <button onClick={() => setProductStep(null)} style={{ marginTop: 8, alignSelf: 'flex-start', background: 'transparent', border: 'none', color: '#6B6760', fontSize: 12, cursor: 'pointer' }}>
              ← Back to task list
            </button>
          </div>
        )}
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #F0EDE8' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: '#1A1A1A' }}>
              {isImport ? 'Add to canvas — Choose Destination' : isBatch
                ? `Clone ${batchCount} Ads — Choose Brand`
                : mode === 'video-clone' ? 'Clone Video Ad — Choose Brand'
                : mode === 'clone-to-canvas' ? 'Clone Ad — Choose Brand' : 'Clone Ad — Choose Destination'}
            </span>
          </div>
          <button onClick={isImporting ? undefined : onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 8, color: '#8C8578' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#F0EDE8'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'none'; }}>
            <X style={{ width: 15, height: 15 }} />
          </button>
        </div>

        {/* Preview */}
        <div style={{ padding: '12px 20px', borderBottom: '1px solid #F0EDE8', display: 'flex', alignItems: 'center', gap: 12 }}>
          {isBatch ? (
            <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
              {templates!.slice(0, 4).map((t, idx) => (
                <img
                  key={idx}
                  src={t.imageUrl}
                  alt={t.name}
                  style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 6, border: '1px solid #E5E2DC' }}
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              ))}
              {batchCount > 4 && (
                <div style={{ width: 36, height: 36, borderRadius: 6, border: '1px solid #E5E2DC', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600, color: '#6B6760', background: '#F5F3EE' }}>
                  +{batchCount - 4}
                </div>
              )}
            </div>
          ) : (
            <img src={imageUrl} alt={imageName} style={{ width: 52, height: 52, objectFit: 'cover', borderRadius: 8, border: '1px solid #E5E2DC', flexShrink: 0 }}
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
          )}
          <div>
            <p style={{ fontSize: 13, fontWeight: 500, color: '#1A1A1A', margin: '0 0 2px' }}>
              {isBatch ? `${batchCount} templates selected` : imageName}
            </p>
            <p style={{ fontSize: 11, color: '#8C8578', margin: 0 }}>
              {isImport ? 'Add the selected reference images to an existing or new canvas' : isBatch
                ? `Each will be cloned ${autoVariationsCount}× in parallel on the brand's canvas`
                : mode === 'video-clone'
                  ? 'The video is analyzed and re-scripted for the brand — you review the script before generating'
                  : mode === 'clone-to-canvas'
                    ? `Dropped on the brand's canvas — ${autoVariationsCount} variation${autoVariationsCount === 1 ? '' : 's'} auto-generate`
                    : 'Image will be added to the selected chat as an attachment'}
            </p>
          </div>
        </div>

        {/* Body — v6.67 reorder (Dan): New Task lives at the TOP with an
            editable prefilled name; the long flat "Existing Tasks" list is
            gone; below sit the BRAND FOLDERS (sidebar folder icon), each
            expandable to its tasks for cloning into an existing canvas. */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '10px 0' }}>
          {/* Create new task */}
          <p style={{ fontSize: 11, fontWeight: 600, color: '#B0ACA6', textTransform: 'uppercase', letterSpacing: '0.06em', padding: '4px 20px 8px' }}>New Task</p>
          <div style={{ padding: '0 20px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
            <input
              data-testid="clone-task-name"
              type="text"
              placeholder="Task name"
              value={newTaskName}
              onChange={(e) => { setNewTaskName(e.target.value); createdImportProject.current = null; }}
              onFocus={(e) => { e.currentTarget.style.borderColor = '#A8A49E'; e.currentTarget.select(); }}
              onBlur={(e) => { e.currentTarget.style.borderColor = '#E5E2DC'; }}
              style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #E5E2DC', background: '#FFFFFF', fontSize: 13, color: '#1A1A1A', outline: 'none', boxSizing: 'border-box' }}
            />
            <select
              data-testid="clone-folder-select"
              value={selectedFolderId ?? ''}
              onChange={(e) => { setSelectedFolderId(e.target.value ? Number(e.target.value) : null); createdImportProject.current = null; }}
              style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #E5E2DC', background: '#F7F6F3', fontSize: 13, color: '#3D3A36', outline: 'none' }}
            >
              <option value="" disabled>Choose a brand</option>
              {folders.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          </div>

          <div style={{ height: 1, background: '#F0EDE8', margin: '2px 0 6px' }} />

          {/* Current chat */}
          {persistenceProjectId && (
            <>
              <button
                data-testid="clone-to-current"
                onClick={handleAddToCurrentChat}
                disabled={isImporting}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '8px 20px', border: 'none', background: hoveredId === -1 ? '#F5F3EE' : 'transparent', color: '#3D3A36', fontSize: 13, cursor: 'pointer', textAlign: 'left' }}
                onMouseEnter={() => setHoveredId(-1)}
                onMouseLeave={() => setHoveredId(null)}
              >
                <Plus style={{ width: 14, height: 14, color: '#8C8578', flexShrink: 0 }} />
                <span>{isImport ? 'Add to current canvas' : 'Add to current chat'}</span>
              </button>
              <div style={{ height: 1, background: '#F0EDE8', margin: '6px 0' }} />
            </>
          )}

          {/* Brand folders → their tasks */}
          <p style={{ fontSize: 11, fontWeight: 600, color: '#B0ACA6', textTransform: 'uppercase', letterSpacing: '0.06em', padding: '4px 20px 6px' }}>Brands</p>

          {isLoading && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 20 }}>
              <Loader2 style={{ width: 18, height: 18, color: '#B0ACA6' }} className="animate-spin" />
            </div>
          )}

          {!isLoading && (
            <div>
              {isImport && allProjects.filter(p => !p.isFolder && !folders.some(f => f.id === p.parentId)).map(task => (
                <button key={task.id} data-testid={`import-task-${task.id}`} disabled={isImporting} onClick={() => handleSelectExistingTask(task.id)} style={{display:'block',width:'100%',textAlign:'left',padding:'8px 20px',border:0,background:'transparent'}}>{task.name}</button>
              ))}
              {folders.map((folder) => (
                <div key={folder.id}>
                  <button
                    data-testid={`clone-folder-toggle-${folder.id}`}
                    onClick={() => toggleFolder(folder.id)}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '8px 20px', border: 'none', background: 'transparent', color: '#1A1A1A', fontSize: 13, cursor: 'pointer', textAlign: 'left', fontWeight: 500 }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#F5F3EE'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
                  >
                    <Folder style={{ width: 14, height: 14, color: '#8C8578', flexShrink: 0 }} />
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{folder.name}</span>
                    <ChevronRight
                      style={{ width: 13, height: 13, color: '#C5C1BB', flexShrink: 0, transition: 'transform 0.15s', transform: expandedFolders.has(folder.id) ? 'rotate(90deg)' : 'rotate(0deg)' }}
                    />
                  </button>
                  {expandedFolders.has(folder.id) && (
                    <>
                      {tasksInFolder(folder.id).map((task) => (
                        <button
                          key={task.id}
                          data-testid={`clone-task-${task.id}`}
                          disabled={isImporting}
                          onClick={() => handleSelectExistingTask(task.id)}
                          style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '7px 20px 7px 44px', border: 'none', background: hoveredId === task.id ? '#F5F3EE' : 'transparent', color: '#5A5755', fontSize: 12.5, cursor: 'pointer', textAlign: 'left' }}
                          onMouseEnter={() => setHoveredId(task.id)}
                          onMouseLeave={() => setHoveredId(null)}
                        >
                          <FileText style={{ width: 12, height: 12, color: '#B0ACA6', flexShrink: 0 }} />
                          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{task.name}</span>
                          <ChevronRight style={{ width: 12, height: 12, color: '#C5C1BB', flexShrink: 0 }} />
                        </button>
                      ))}
                      {tasksInFolder(folder.id).length === 0 && (
                        <p style={{ fontSize: 12, color: '#B0ACA6', padding: '4px 20px 8px 44px', margin: 0 }}>No tasks in this brand yet</p>
                      )}
                    </>
                  )}
                </div>
              ))}

              {folders.length === 0 && (
                <p style={{ fontSize: 13, color: '#B0ACA6', textAlign: 'center', padding: '16px 20px' }}>{isImport ? 'Add a brand to create a canvas' : 'Add a brand before cloning ads'}</p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 20px', borderTop: '1px solid #F0EDE8' }}>
          <button
            data-testid="clone-create-task"
            onClick={handleCloneToNew}
            disabled={isCreating || isImporting || !selectedFolderId}
            style={{ width: '100%', padding: '10px 0', borderRadius: 10, border: 'none', background: isCreating ? '#E8E5E0' : '#A9315B', color: isCreating ? '#B0ACA6' : '#FFFFFF', fontSize: 13.5, fontWeight: 600, cursor: isCreating ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
          >
            {isCreating || isImporting
              ? <><Loader2 style={{ width: 14, height: 14 }} className="animate-spin" />{isImporting ? 'Adding images…' : 'Creating…'}</>
              : isImport ? 'Create Canvas & Add Images' : isBatch
                ? `Create Task & Clone ${batchCount} Ads`
                : mode === 'video-clone'
                  ? 'Create Task & Clone Video Ad'
                  : mode === 'clone-to-canvas'
                    ? `Create Task & Generate ${autoVariationsCount} Variation${autoVariationsCount === 1 ? '' : 's'}`
                    : 'Create New Task & Add Attachment'}
          </button>
        </div>
      </div>
    </div>
  );
}
